import React, { useState } from 'react';
import { COMPANY_INFO, SERVICES_LIST } from '../data/mockData';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    service: '',
    message: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    // Check honeypot
    if (formData.website_url) {
      // Bot detected: simulate success without calling endpoint
      setStatus('success');
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dkwebform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_email: COMPANY_INFO.email,
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          company: formData.company,
          service: formData.service || 'Generel henvendelse',
          message: formData.message,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          company: '',
          service: '',
          message: '',
          website_url: ''
        });
      } else {
        const data = await response.json().catch(() => ({}));
        setStatus('error');
        setErrorMessage(data.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring til os.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      // Fallback friendly message
      setStatus('error');
      setErrorMessage('Afsendelsen kunne ikke gennemføres. Du er velkommen til at kontakte os direkte på tlf. 22 72 87 23 eller mail k-to@outlook.dk.');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 md:p-10 shadow-lg">
      <div className="mb-6">
        <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">
          Send en henvendelse
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          Udfyld formularen, så vender vi hurtigt tilbage. Henvendelser sendes direkte til <strong className="text-slate-800">{COMPANY_INFO.email}</strong>.
        </p>
      </div>

      {status === 'success' ? (
        <div className="p-6 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 space-y-3 animate-in fade-in duration-300">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg">
              <i className="fa-solid fa-check" aria-hidden="true"></i>
            </div>
            <div>
              <h4 className="font-bold text-lg text-emerald-950">Tak for din henvendelse!</h4>
              <p className="text-xs text-emerald-800">Vi har modtaget din besked og kontakter dig hurtigst muligt.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="mt-3 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs rounded-lg transition-colors"
          >
            Send endnu en besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot Anti-spam input (ABSOLUTELY HIDDEN) */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Website URL</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex="-1"
              autoComplete="off"
              value={formData.website_url}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Navn */}
            <div>
              <label htmlFor="name" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Navn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                placeholder="Dit fulde navn"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-slate-50 text-slate-900 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors"
              />
            </div>

            {/* Telefonnummer */}
            <div>
              <label htmlFor="phone" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                placeholder="Tlf. nr."
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-slate-50 text-slate-900 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                E-mailadresse <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                placeholder="din@email.dk"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-slate-50 text-slate-900 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors"
              />
            </div>

            {/* Virksomhedsnavn (valgfrit) */}
            <div>
              <label htmlFor="company" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Virksomhedsnavn <span className="text-slate-400 font-normal">(Valgfrit)</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                placeholder="Virksomhed ApS"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-slate-50 text-slate-900 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors"
              />
            </div>
          </div>

          {/* Valg af ydelse (valgfrit) */}
          <div>
            <label htmlFor="service" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
              Valg af ydelse <span className="text-slate-400 font-normal">(Valgfrit)</span>
            </label>
            <select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-slate-50 text-slate-900 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors"
            >
              <option value="">Vælg en ydelse (eller generel henvendelse)</option>
              {SERVICES_LIST.map((srv) => (
                <option key={srv.slug} value={srv.name}>{srv.name}</option>
              ))}
            </select>
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="message" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
              Beskrivelse af opgaven <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows="4"
              placeholder="Beskriv kort jeres ventilationsanlæg, opgave eller ønsker til service/fejlfinding..."
              value={formData.message}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-slate-50 text-slate-900 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors"
            ></textarea>
          </div>

          {status === 'error' && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-medium">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full sm:w-auto px-8 py-4 bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white font-extrabold text-sm uppercase tracking-wider rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {status === 'submitting' ? (
              <>
                <i className="fa-solid fa-spinner animate-spin" aria-hidden="true"></i>
                <span>Sender henvendelse...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
                <span>Send henvendelse</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
